import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputAdornment,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { ArrowBack, Payment, Receipt, ReplayCircleFilled, ExitToApp } from '@mui/icons-material';
import { useForm } from 'react-hook-form';
import { toast } from 'react-toastify';

import { spAPI } from '../../services/api';
import { StatusChip } from '../../components/common/DataTable';

const fmt = (n) => new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);

const STATUS_ACTIONS = {
  draft: [{ value: 'confirmed', label: 'Confirm Order', color: 'primary' }, { value: 'cancelled', label: 'Cancel Order', color: 'error' }],
  confirmed: [{ value: 'processing', label: 'Mark Processing', color: 'info' }, { value: 'cancelled', label: 'Cancel Order', color: 'error' }],
  processing: [{ value: 'cancelled', label: 'Cancel Order', color: 'error' }],
  paid: [{ value: 'returned', label: 'Return Order', color: 'warning' }],
};

const ExitTicketDialog = ({ open, onClose, order }) => {
  const [loading, setLoading] = useState(false);

  const handlePrintTicket = () => {
    try {
      setLoading(true);
      const ticketContent = `
      <div style="font-family: monospace; padding: 20px; max-width: 400px; margin: 0 auto;">
        <div style="text-align: center; border-bottom: 2px solid #000; padding-bottom: 10px; margin-bottom: 10px;">
          <h2 style="margin: 0;">EXIT PREMISES TICKET</h2>
          <p style="margin: 5px 0;">AUTHORIZED SPARE PARTS REMOVAL</p>
        </div>
        <div style="margin-bottom: 15px;">
          <div><strong>Order #:</strong> ${order.order_number}</div>
          <div><strong>Date:</strong> ${new Date().toLocaleString()}</div>
        </div>
        <div style="border: 1px solid #000; padding: 10px; margin-bottom: 15px;">
          <div><strong>Customer:</strong> ${order.customer_name || 'N/A'}</div>
          <div><strong>Total Amount:</strong> ${fmt(order.total_amount)}</div>
          <div><strong>Paid Amount:</strong> ${fmt(order.paid_amount)}</div>
        </div>
        <div style="text-align: center; margin-top: 20px;">
          <p style="margin: 5px 0;">Signature: ________________________</p>
        </div>
      </div>
      `.trim();
      const ticketText = ticketContent.replace(/<[^>]*>/g, '\n').replace(/\n\s*\n/g, '\n');
      const blob = new Blob([ticketText], { type: 'text/plain' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `exit-ticket-sp-${order.order_number}.txt`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('Exit ticket downloaded successfully');
    } catch (err) {
      toast.error('Failed to download ticket');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle fontWeight={700}>Exit Premises Ticket</DialogTitle>
      <DialogContent dividers>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          This ticket authorizes the removal of spare parts from the premises.
        </Typography>
        <Typography variant="body1" sx={{ mb: 1 }}><strong>Order:</strong> {order.order_number}</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}><strong>Customer:</strong> {order.customer_name || 'N/A'}</Typography>
        <Typography variant="body1" sx={{ mb: 1 }}><strong>Total Amount:</strong> {fmt(order.total_amount)}</Typography>
        <Typography variant="body1" sx={{ mb: 2 }}><strong>Paid Amount:</strong> {fmt(order.paid_amount)}</Typography>
      </DialogContent>
      <DialogActions sx={{ p: 2 }}>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          variant="contained"
          startIcon={loading ? <CircularProgress size={20} /> : <Payment />}
          onClick={handlePrintTicket}
          disabled={loading}
        >
          {loading ? 'Generating...' : 'Download Ticket'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const PaymentDialog = ({ open, onClose, order, onSaved }) => {
  const { register, handleSubmit, reset, formState: { errors } } = useForm();
  const [loading, setLoading] = useState(false);
  const [paymentMethods, setPaymentMethods] = useState([]);

  useEffect(() => {
    if (!open) return;

    reset({ amount: order?.balance_due || 0, payment_method_id: '', reference_number: '', bank_name: '', notes: '' });
    spAPI.getPaymentMethods()
      .then((response) => setPaymentMethods(response.data.data || []))
      .catch(() => {
        setPaymentMethods([
          { id: 'cash', name: 'Cash' },
          { id: 'bank_transfer', name: 'Bank Transfer' },
          { id: 'mobile_money', name: 'Mobile Money' },
        ]);
      });
  }, [open, order, reset]);

  const onSubmit = async (data) => {
    try {
      setLoading(true);
      await spAPI.recordPayment(order.id, {
        amount: parseFloat(data.amount),
        payment_method_id: data.payment_method_id || null,
        reference_number: data.reference_number || '',
        bank_name: data.bank_name || '',
        notes: data.notes || '',
      });
      toast.success('Payment recorded');
      onSaved();
      onClose();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to record payment');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <form onSubmit={handleSubmit(onSubmit)}>
        <DialogTitle fontWeight={700}>Record Payment</DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Amount *"
                type="number"
                {...register('amount', {
                  required: 'Amount is required',
                  min: { value: 0.01, message: 'Amount must be greater than zero' },
                  max: { value: order?.balance_due || 0, message: `Cannot exceed ${fmt(order?.balance_due)}` },
                  valueAsNumber: true,
                })}
                error={!!errors.amount}
                helperText={errors.amount?.message || `Outstanding balance: ${fmt(order?.balance_due)}`}
                InputProps={{ startAdornment: <InputAdornment position="start">$</InputAdornment> }}
              />
            </Grid>
            <Grid item xs={12}>
              <FormControl fullWidth size="small">
                <InputLabel>Payment Method</InputLabel>
                <Select {...register('payment_method_id')} label="Payment Method" defaultValue="">
                  <MenuItem value="">Select method</MenuItem>
                  {paymentMethods.map((method) => (
                    <MenuItem key={method.id} value={method.id}>{method.name}</MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Reference Number" {...register('reference_number')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth label="Bank / Mobile Wallet" {...register('bank_name')} />
            </Grid>
            <Grid item xs={12}>
              <TextField fullWidth multiline rows={2} label="Notes" {...register('notes')} />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          <Button onClick={onClose}>Cancel</Button>
          <Button type="submit" variant="contained" startIcon={<Payment />} disabled={loading}>
            {loading ? <CircularProgress size={20} /> : 'Record Payment'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

const SparePartsOrderDetailPage = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [paymentOpen, setPaymentOpen] = useState(false);
  const [exitTicketOpen, setExitTicketOpen] = useState(false);
  const [statusLoading, setStatusLoading] = useState(null);

  const fetchOrder = useCallback(async () => {
    try {
      setLoading(true);
      const { data } = await spAPI.getOrder(id);
      setOrder(data.data);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to load order');
    } finally {
      setLoading(false);
    }
  }, [id]);

  useEffect(() => { fetchOrder(); }, [fetchOrder]);

  const handleStatusChange = async (status) => {
    try {
      setStatusLoading(status);
      await spAPI.updateOrderStatus(id, status);
      toast.success(`Order ${status.replace(/_/g, ' ')}`);
      fetchOrder();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to update order status');
    } finally {
      setStatusLoading(null);
    }
  };

  const handleDownloadInvoice = async () => {
    try {
      const response = await spAPI.downloadInvoice(id);
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${order.invoice_number || order.order_number}.pdf`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (err) {
      toast.error(err.response?.data?.message || 'Failed to download invoice');
    }
  };

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', mt: 8 }}><CircularProgress /></Box>;
  }

  if (!order) {
    return <Alert severity="error">Order not found</Alert>;
  }

  const actions = STATUS_ACTIONS[order.status] || [];

  return (
    <Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
        <Button startIcon={<ArrowBack />} onClick={() => navigate('/sp/orders')}>Back</Button>
        <Box sx={{ flexGrow: 1 }}>
          <Typography variant="h4" fontWeight={700}>Order {order.order_number}</Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date(order.created_at).toLocaleString()}
          </Typography>
        </Box>
        <StatusChip status={order.payment_status} />
        <StatusChip status={order.status} />
      </Box>

      <Stack direction="row" spacing={2} sx={{ mb: 3, flexWrap: 'wrap' }}>
        {actions.map((action) => (
          <Button
            key={action.value}
            variant="contained"
            color={action.color || 'primary'}
            disabled={statusLoading === action.value}
            onClick={() => handleStatusChange(action.value)}
          >
            {statusLoading === action.value ? <CircularProgress size={20} /> : action.label}
          </Button>
        ))}
        {order.balance_due > 0 && !['cancelled', 'returned'].includes(order.status) && (
          <Button variant="outlined" startIcon={<Payment />} onClick={() => setPaymentOpen(true)}>
            Record Payment
          </Button>
        )}
        {order.status === 'paid' && (
          <Button variant="contained" color="success" startIcon={<ExitToApp />} onClick={() => setExitTicketOpen(true)}>
            Exit Ticket
          </Button>
        )}
        {order.status === 'paid' && (
          <Button variant="outlined" startIcon={<Receipt />} onClick={handleDownloadInvoice}>
            Download Invoice
          </Button>
        )}
      </Stack>

      <Grid container spacing={3}>
        <Grid item xs={12} md={8}>
          <Card sx={{ mb: 3 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Order Items</Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Part</TableCell>
                    <TableCell>SKU</TableCell>
                    <TableCell>Part #</TableCell>
                    <TableCell>Location</TableCell>
                    <TableCell align="right">Qty</TableCell>
                    <TableCell align="right">Unit Price</TableCell>
                    <TableCell align="right">Total</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(order.items || []).map((item) => (
                    <TableRow key={item.id}>
                      <TableCell fontWeight={600}>{item.product_name}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.sku}</TableCell>
                      <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{item.part_number || 'N/A'}</TableCell>
                      <TableCell>{item.location_code || 'N/A'}</TableCell>
                      <TableCell align="right">{item.quantity}</TableCell>
                      <TableCell align="right">{fmt(item.unit_price)}</TableCell>
                      <TableCell align="right" sx={{ fontWeight: 700 }}>{fmt(item.line_total)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: 2 }}>
                <Box sx={{ minWidth: 260 }}>
                  {[
                    { label: 'Subtotal', value: fmt(order.subtotal) },
                    { label: 'Discount', value: `-${fmt(order.discount_amount)}`, color: 'success.main' },
                    { label: 'Tax', value: fmt(order.tax_amount) },
                  ].map((row) => (
                    <Box key={row.label} sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="body2">{row.label}:</Typography>
                      <Typography variant="body2" color={row.color}>{row.value}</Typography>
                    </Box>
                  ))}
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 1 }}>
                    <Typography fontWeight={700}>Total:</Typography>
                    <Typography fontWeight={700}>{fmt(order.total_amount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="success.main">Paid:</Typography>
                    <Typography variant="body2" color="success.main">{fmt(order.paid_amount)}</Typography>
                  </Box>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="error.main" fontWeight={700}>Balance:</Typography>
                    <Typography variant="body2" color="error.main" fontWeight={700}>{fmt(order.balance_due)}</Typography>
                  </Box>
                </Box>
              </Box>
            </CardContent>
          </Card>

          {(order.payments || []).length > 0 && (
            <Card>
              <CardContent>
                <Typography variant="h6" fontWeight={700} gutterBottom>Payment History</Typography>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>Payment #</TableCell>
                      <TableCell>Date</TableCell>
                      <TableCell>Method</TableCell>
                      <TableCell align="right">Amount</TableCell>
                      <TableCell>Status</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {order.payments.map((payment) => (
                      <TableRow key={payment.id}>
                        <TableCell sx={{ fontFamily: 'monospace', fontSize: '0.8rem' }}>{payment.payment_number}</TableCell>
                        <TableCell>{new Date(payment.payment_date).toLocaleDateString()}</TableCell>
                        <TableCell>{payment.payment_method_name || 'N/A'}</TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: 'success.main' }}>{fmt(payment.amount)}</TableCell>
                        <TableCell><StatusChip status={payment.status} /></TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          )}
        </Grid>

        <Grid item xs={12} md={4}>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Customer</Typography>
              <Typography fontWeight={600}>{order.customer_name || 'Walk-in customer'}</Typography>
              <Typography variant="body2" color="text.secondary">{order.customer_email || 'No email'}</Typography>
              <Typography variant="body2" color="text.secondary">{order.customer_phone || 'No phone'}</Typography>
            </CardContent>
          </Card>
          <Card sx={{ mb: 2 }}>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Order Meta</Typography>
              <Typography variant="body2">Invoice: {order.invoice_number}</Typography>
              <Typography variant="body2">Warehouse: {order.warehouse_name || 'N/A'}</Typography>
              <Typography variant="body2">Salesperson: {order.salesperson_name || 'N/A'}</Typography>
              <Typography variant="body2">Type: {order.order_type}</Typography>
            </CardContent>
          </Card>
          <Card>
            <CardContent>
              <Typography variant="h6" fontWeight={700} gutterBottom>Notes</Typography>
              <Typography variant="body2" color="text.secondary">{order.notes || 'No notes'}</Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      <PaymentDialog open={paymentOpen} onClose={() => setPaymentOpen(false)} order={order} onSaved={fetchOrder} />
      <ExitTicketDialog open={exitTicketOpen} onClose={() => setExitTicketOpen(false)} order={order} />
    </Box>
  );
};

export default SparePartsOrderDetailPage;
